import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";
import mitt from "mitt";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // Handles UTC time conversions
import timezone from "dayjs/plugin/timezone"; // Handles timezone conversions
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"; // Handles isSameOrAfter method
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"; // Handles isSameOrBefore method
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import {
  isAppleMobileDevice,
  openInPreferredBrowser,
  shouldOfferBrowserRedirect,
} from "./utils/browserDetection";
// Import global components
import BaseButton from "./components/ui/basebutton.vue";
import BaseCard from "./components/ui/basecard.vue";
import BaseDialog from "./components/ui/basedialog.vue";
import BaseDialogConfirm from "./components/ui/basedialogconfirm.vue";
import BaseErrorSuccessMessage from "./components/ui/baseerrorsuccessmessage.vue";
import BaseIcon from "./components/ui/baseicon.vue";
import BaseLoader from "./components/ui/baseloader.vue";
import BaseNotificationCard from "./components/ui/basenotificationcard.vue";
import BaseTooltipInfo from "./components/ui/basetooltipinfo.vue";
import VideoCard from "./components/ui/videocard.vue";

const appInsights = new ApplicationInsights({
  config: {
    connectionString:
      "InstrumentationKey=60dcfd5e-a767-4c0c-9882-1505bc90774c;IngestionEndpoint=https://westeurope-5.in.applicationinsights.azure.com/;LiveEndpoint=https://westeurope.livediagnostics.monitor.azure.com/;ApplicationId=d11ec443-0143-46bd-b947-1a7b9278c99b",
  },
});
appInsights.loadAppInsights();
window.appInsights = appInsights;

function renderIOSRedirectScreen() {
  document.body.setAttribute(
    "style",
    [
      "margin:0",
      "min-height:100vh",
      "background:#f5f7fb",
      "font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      "display:flex",
      "align-items:center",
      "justify-content:center",
      "padding:24px",
      "text-align:center",
    ].join(";")
  );
  document.body.innerHTML = `
    <div style="display:flex; align-items:center; justify-content:center; width:100%;">
      <div style="
        max-width: 420px;
        width: 100%;
        background: #ffffff;
        border-radius: 16px;
        box-shadow: 0 12px 30px rgba(15, 23, 42, 0.15);
        padding: 32px 28px;
      ">
        <div style="
          width: 68px;
          height: 68px;
          border-radius: 50%;
          background: #111827;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 18px;
          color: #fff;
          font-weight: 600;
          font-size: 20px;
        ">
          NT
        </div>
        <h2 style="margin: 0 0 12px; color: #111827; font-size: 22px;">
          Open NetworkTal in your browser
        </h2>
        <p style="color: #4b5563; margin: 0 0 18px; line-height: 1.5;">
          This in-app browser (LinkedIn, X, Instagram, etc.) cannot load the full platform.
          Tap the button below to continue in Safari or your default browser.
        </p>
        <button
          style="
            background: #111827;
            color: #fff;
            border: none;
            border-radius: 10px;
            padding: 14px 16px;
            font-size: 16px;
            font-weight: 500;
            width: 100%;
            cursor: pointer;
          "
          onclick="window.openInPreferredBrowser && window.openInPreferredBrowser()"
        >
          Open in Browser
        </button>
        <p style="color: #6b7280; margin: 16px 0 0; font-size: 14px;">
          If nothing happens, use the share menu inside this app and choose “Open in Safari/Chrome”.
        </p>
      </div>
    </div>
  `;
}

function showIOSRedirectPrompt(reason = null) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", renderIOSRedirectScreen);
  } else {
    renderIOSRedirectScreen();
  }

  window.openInPreferredBrowser = () => openInPreferredBrowser();

  appInsights.trackEvent({
    name: "IOSWebviewRedirectPrompt",
    properties: reason ? { reason } : undefined,
  });

  // GA4 page_view (this path does not mount Vue/router, so router.afterEach won't run)
  try {
    window.gtag?.("event", "page_view", {
      page_path: window.location.pathname + window.location.search,
      page_title: document.title,
    });
  } catch (e) {
    // ignore
  }
}

if (shouldOfferBrowserRedirect()) {
  showIOSRedirectPrompt("ua_detected_in_app_browser");
} else {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(isSameOrAfter);
  dayjs.extend(isSameOrBefore);
  dayjs.tz.setDefault("UTC");

  let bootstrapError = null;
  const bootstrapErrorHandler = (
    eventOrError,
    source,
    lineno,
    colno,
    error
  ) => {
    // capture the first error only (avoid spam)
    if (bootstrapError) return;

    bootstrapError =
      error ||
      eventOrError?.error ||
      (eventOrError instanceof Error ? eventOrError : null);

    try {
      appInsights.trackException({
        exception:
          bootstrapError ||
          new Error(
            typeof eventOrError === "string"
              ? eventOrError
              : "Webview bootstrap error"
          ),
        properties: {
          phase: "bootstrap",
          source,
          lineno,
          colno,
          path: window.location.pathname + window.location.search,
        },
      });
    } catch (e) {
      // ignore
    }
  };

  const rejectionHandler = (event) => {
    bootstrapErrorHandler(
      event?.reason || "unhandledrejection",
      "unhandledrejection"
    );
  };

  window.addEventListener("error", bootstrapErrorHandler);
  window.addEventListener("unhandledrejection", rejectionHandler);

  const app = createApp(App);
  const eventBus = mitt();

  app.component("base-button", BaseButton);
  app.component("base-card", BaseCard);
  app.component("base-dialog", BaseDialog);
  app.component("base-dialog-confirm", BaseDialogConfirm);
  app.component("base-error-success-message", BaseErrorSuccessMessage);
  app.component("base-icon", BaseIcon);
  app.component("base-loader", BaseLoader);
  app.component("base-notification-card", BaseNotificationCard);
  app.component("base-tooltip-info", BaseTooltipInfo);
  app.component("video-card", VideoCard);

  app.provide("eventBus", eventBus);
  app.provide("dayjs", dayjs);

  // If the SPA never mounts (common in some iOS in-app webviews), avoid a blank page.
  // After a short grace period, show the "Open in Browser" prompt instead.
  setTimeout(() => {
    const didMount = document.documentElement.dataset.ntMounted === "1";
    if (didMount) return;

    // Only apply the fallback on iOS devices; desktop slow loads should not be affected.
    try {
      if (isAppleMobileDevice()) {
        showIOSRedirectPrompt(
          bootstrapError ? "bootstrap_error" : "mount_timeout"
        );
      }
    } catch (e) {
      // ignore
    }
  }, 4000);

  try {
    app.use(router).use(store).use(vuetify).mount("#app");

    // Mark successful mount.
    document.documentElement.dataset.ntMounted = "1";
    window.__ntMounted = true;

    // If AppCheck is enforced and token retrieval fails, Firestore calls will be blocked.
    // Await the AppCheck readiness signal (set by firebase-init.js) and show a user-visible
    // fallback ONLY on Apple mobile devices (where the in-app webview issue occurs).
    try {
      if (isAppleMobileDevice() && window.__ntAppCheckReady?.then) {
        window.__ntAppCheckReady.then((ok) => {
          if (ok) return;
          try {
            showIOSRedirectPrompt("appcheck_failed");
          } catch (e) {
            // ignore
          }
        });
      }
    } catch (e) {
      // ignore
    }

    // Once mounted, we no longer need global bootstrap error listeners.
    setTimeout(() => {
      window.removeEventListener("error", bootstrapErrorHandler);
      window.removeEventListener("unhandledrejection", rejectionHandler);
    }, 10000);
  } catch (error) {
    bootstrapErrorHandler("Vue mount threw", "mount", null, null, error);

    try {
      if (isAppleMobileDevice()) {
        showIOSRedirectPrompt("mount_exception");
      }
    } catch (e) {
      // ignore
    }
  }
}
